<template>
    <v-container fluid class="selector-container pa-3">
        <v-layout fill-height>

            <!-- Left panel: all options -->
            <v-flex xs12 sm5 md4 class="d-flex flex-column pr-2">
                <v-card color="primary" class="pa-3">
                    <v-card-title class="title white--text py-1">Transition</v-card-title>
                    <v-card-text>
                        <v-select
                            v-model="selectedTo"
                            :items="companies"
                            item-text="title"
                            item-value="id"
                            label="Transition To"
                            color="secondary"
                            hide-details
                            @change="selectedSubType = null"
                        ></v-select>

                        <v-select
                            v-model="selectedSubType"
                            :items="subTypes"
                            label="Grade"
                            color="secondary"
                            :disabled="!subTypes.length"
                            :hint="selectedTo && !subTypes.length ? 'Not required' : ''"
                            persistent-hint
                            class="mt-3"
                        ></v-select>

                        <v-divider class="mt-4 mb-2" dark></v-divider>

                        <v-autocomplete
                            :items="allPrinters"
                            v-model="selectedPrinter"
                            color="secondary"
                            label="Select Printer"
                            box
                            hide-details
                            clearable
                            :disabled="!ready"
                            class="mt-2"
                        ></v-autocomplete>

                        <v-select
                            v-if="selectedTo === 'bohm'"
                            :items="['medium', 'small']"
                            v-model="selectedSize"
                            color="secondary"
                            label="Label Size"
                            box
                            hide-details
                            :disabled="!ready"
                            class="mt-3"
                        ></v-select>

                        <v-checkbox
                            v-model="autoprint"
                            color="secondary"
                            label="Autoprint Labels"
                            hide-details
                            :disabled="!ready"
                            class="mt-2"
                        ></v-checkbox>

                        <v-alert
                            v-if="!ready"
                            type="info"
                            outline
                            class="mt-3 mb-0"
                            style="font-size: 13px;"
                        >
                            {{ statusHint }}
                        </v-alert>
                    </v-card-text>
                </v-card>
            </v-flex>

            <!-- Right panel: barcode scanner fills remaining height -->
            <v-flex xs12 sm7 md8 :class="{ 'section-disabled': !ready }" class="pl-2">
                <Barcode
                    :backgroundColor="'primary'"
                    :title="ready ? transitionLabel : 'Complete setup'"
                    subtitle="Scan or Enter a Serial Number"
                    buttonText="Transition"
                    height="100%"
                    @search="transitionProduct"
                    ref="barcodeComponent"
                />
            </v-flex>

        </v-layout>
    </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LoginCheck from "@/mixins/LoginCheck";
import Barcode from "@/components/app/Barcode";

export default {
    name: "transition",

    components: { Barcode },

    mixins: [LoginCheck],

    data() {
        return {
            selectedTo: null,
            selectedSubType: null,
            companies: [
                { id: "bohm",    title: "Bohm" },
                { id: "asurion", title: "Asurion" },
                { id: "vzd",     title: "Verizon Dropship" },
                // { id: "ctdi",    title: "CTDI" },
            ],
            subTypeMap: {
                vzd:  ["A", "B", "C"],
                bohm: ["Certified Refurbished", "Refurbished", "Pre-Owned"],
            },
        };
    },

    computed: {
        ...mapGetters({
            storedPrinter:   "repair/getSelectedPrinter",
            storedAutoprint: "repair/getAutoPrint",
            storedPrintSize: "repair/getStoredPrintSize",
            allPrinters:     "repair/getAllPrinters",
            user:            "app/getUser",
        }),

        selectedPrinter: {
            get() { return this.storedPrinter; },
            set(val) { this.$store.commit("repair/setSelectedPrinter", val); },
        },

        autoprint: {
            get() { return this.storedAutoprint; },
            set(val) { this.$store.commit("repair/setAutoPrint", val); },
        },

        selectedSize: {
            get() { return this.storedPrintSize; },
            set(val) { this.$store.commit("repair/setStoredPrintSize", val); },
        },

        subTypes() {
            return this.subTypeMap[this.selectedTo] || [];
        },

        ready() {
            if (!this.selectedTo) return false;
            if (this.subTypes.length && !this.selectedSubType) return false;
            return true;
        },

        statusHint() {
            if (!this.selectedTo) return "Select a transition type to continue.";
            if (this.subTypes.length && !this.selectedSubType) return "Select a grade to continue.";
            return "";
        },

        transitionLabel() {
            const to  = this.companies.find(c => c.id === this.selectedTo);
            const sub = this.selectedSubType ? ` (${this.selectedSubType})` : "";
            return `${to ? to.title : ""}${sub}`;
        },
    },

    async beforeMount() {
        await this.getAllPrinters();
    },

    methods: {
        ...mapActions({
            transitionRepair:  "repair/transitionRepair",
            printProductLabel: "repair/printProductLabel",
            printVZDLabel:     "repair/printVZDLabel",
            printCTDILabel:    "repair/printCTDILabel",
            getAllPrinters:    "repair/getAllPrinters",
        }),

        focusBarcode() {
            this.$refs.barcodeComponent && this.$refs.barcodeComponent.focusBarcode();
        },

        async transitionProduct(barcodeValue) {
            if (this.autoprint && !this.selectedPrinter) {
                this.$store.dispatch("app/setSnackbar", {
                    text: "Please select a printer before scanning.",
                    color: "red",
                });
                return;
            }

            const res = await this.transitionRepair({
                name:      barcodeValue,
                type:      this.selectedTo,
                condition: this.selectedSubType,
            });

            if (!res.success) {
                this.$store.dispatch("app/setSnackbar", { text: res.message, color: "red" });
                return;
            }

            if (this.autoprint) {
                if (this.selectedTo === "asurion") {
                    await this.printProductLabel({
                        printer:     this.selectedPrinter,
                        sn:          res.repair_name,
                        sku:         res.new_product.sku,
                        barcode:     res.new_product.barcode,
                        description: res.new_product.desc,
                        owner:       res.owner,
                        size:        "small",
                    });
                } else if (this.selectedTo === "vzd") {
                    this.printVZDLabel({
                        printer: this.selectedPrinter,
                        sku:     res.new_product.sku,
                        serial:  res.repair_name,
                        barcode: res.new_product.barcode,
                        desc:    res.new_product.desc,
                    });
                } else if (this.selectedTo === "ctdi") {
                    await this.printCTDILabel({
                        printer: this.selectedPrinter,
                        sku:     res.new_product.sku,
                        serial:  res.imei,
                        model:   res.model,
                    });
                } else if (this.selectedTo === "bohm") {
                    await this.printProductLabel({
                        printer:     this.selectedPrinter,
                        sn:          res.repair_name,
                        sku:         res.new_product.sku,
                        barcode:     res.new_product.barcode,
                        description: res.new_product.desc,
                        owner:       res.owner,
                        size:        this.selectedSize,
                    });
                }
            } else {
                this.$store.dispatch("app/setSnackbar", { text: "Transition successful.", color: "green" });
            }

            this.focusBarcode();
        },
    },
};
</script>

<style>
.selector-container {
    height: 100%;
}

.section-disabled {
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
}
</style>
